import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { AdminApiError, adminApi } from "./api.js";

type AuthStatus = "checking" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  status: AuthStatus;
  login: (
    password: string,
  ) => Promise<{ ok: boolean; error?: string | undefined; retryAfterSeconds?: number | undefined }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("checking");

  useEffect(() => {
    adminApi
      .get("/api/admin/session")
      .then(() => setStatus("authenticated"))
      .catch(() => setStatus("unauthenticated"));
  }, []);

  const login = useCallback(async (password: string) => {
    try {
      await adminApi.post("/api/admin/login", { password });
      setStatus("authenticated");
      return { ok: true };
    } catch (err) {
      if (err instanceof AdminApiError) {
        if (err.status === 429) {
          const body = err.body as { retryAfterSeconds?: number };
          return { ok: false, error: "TOO_MANY_ATTEMPTS", retryAfterSeconds: body.retryAfterSeconds };
        }
        return { ok: false, error: "INVALID_PASSWORD" };
      }
      return { ok: false, error: "UNKNOWN" };
    }
  }, []);

  const logout = useCallback(async () => {
    await adminApi.post("/api/admin/logout").catch(() => undefined);
    setStatus("unauthenticated");
  }, []);

  return <AuthContext.Provider value={{ status, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAdminAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
